import { AuthService } from './auth.service';
import { HttpClient} from '@angular/common/http';
import { Globals } from '../shared/globals';
import { Permission } from '../models/permission.model';
import { FeatureService } from './feature.service';
import { Injectable } from "@angular/core";
import { JsonSerializer } from 'typescript-json-serializer';

/**
 * Service responsible for managing permissions.
 *
 * @export
 * @class PermissionService
 */
@Injectable()
export class PermissionService {
  private jsonSerializer = new JsonSerializer();

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private globals: Globals,
    private featureService: FeatureService
  ) { }

  /**
   * Creates permissions.
   *
   * @returns List of created permissions.
   * @memberof PermissionService
   */
  public createPermissions(permissions: Permission[]): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performCreatePermissionsRequest(token, permissions);
    });
  }

  /**
   * Fetches list of available permission types for the given user.
   *
   * @returns List of permission types that can target given resource type.
   * @memberof PermissionService
   */
  public getPermissionTypes(resourceType: string): Promise<string[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetPermissionTypesRequest(token, resourceType);
    });
  }

  /**
   * Fetches permissions targeting given station group.
   *
   * @returns list of permissions
   * @memberof PermissionService
   */
  public getStationGroupPermissions(groupId: string): Promise<Permission[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetPermissionsRequest(token, 'station-group', groupId);
    });
  }

  /**
   * Fetches permissions that are related to the given user in any way -
   * permissions where the user is an assignee, target, or a group he belongs to is
   * an assignee or a target.
   *
   * @returns list of permissions
   * @memberof PermissionService
   */
  public getPermissionsRelatedToUser(): Promise<Permission[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetPermissionsRelatedToUserRequest(token);
    });
  }

  public deletePermissions(permissions: Permission[]): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performDeletePermissionsRequest(token, permissions);
    });
  }

  public getUserIdForEmailAddress(emailAddress: string): Promise<string> {
    return this.authService.getToken().then((token) => {
      return this.performGetUserIdForEmailAddress(token, emailAddress);
    });
  }

  public getOcppPermissions(): Promise<Permission[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetOcppPermissionsRequest(token);
    });
  }

  public getQrCodeGenerationPermissionsRequest(): Promise<Permission[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetQrCodeGenerationPermissionsRequest(token);
    });
  }

  public getMaintenancePermissionsRequest(): Promise<Permission[]> {
    return this.authService.getToken().then((token) => {
      return this.getPermissionList(token, 'maintenance');
    });
  }

  // Internal methods

  private performCreatePermissionsRequest(
    token: string,
    permissions: Permission[]
  ): Promise<void> {
    const url = new URL(this.globals.apiUrl('permissions'));
    const payload = this.jsonSerializer.serializeObjectArray(permissions);

    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), payload, { headers: this.authService.headers(token) })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetPermissionTypesRequest(
    token: string,
    resourceType: string
  ): Promise<string[]> {
    const params = new URLSearchParams({ resourcetype: resourceType });
    const url = new URL(
      this.globals.apiUrl(`permissions/types?` + params.toString())
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = response as string[];
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetPermissionsRequest(
    token: string,
    resourceType: string,
    resourceId: string
  ): Promise<Permission[]> {
    const params = new URLSearchParams({
      resourcetype: resourceType,
      resourceid: resourceId,
    });
    const url = new URL(
      this.globals.apiUrl(`permissions?` + params.toString())
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response:string) => {
          resolve(this.jsonSerializer.deserializeObjectArray(response, Permission));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetOcppPermissionsRequest(
    token: string
  ): Promise<Permission[]> {
    const userId = this.authService.userId;
    const url = new URL(
      this.globals.apiUrl('users/' + userId + '/ocpp/permissions')
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<Permission>(response[item], Permission)
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetQrCodeGenerationPermissionsRequest(
    token: string
  ): Promise<Permission[]> {
    const userId = this.authService.userId;
    const url = new URL(
      this.globals.apiUrl('users/' + userId + '/qr-codes/permissions')
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<Permission>(response[item], Permission)
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getPermissionList(
    token: string,
    perm: string
  ): Promise<Permission[]> {
    const userId = this.authService.userId;
    const url = new URL(
      this.globals.apiUrl('users/' + userId + '/' + perm + '/permissions')
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<Permission>(response[item], Permission)
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performDeletePermissionsRequest(
    token: string,
    permissions: Permission[]
  ): Promise<void> {
    const url = new URL(this.globals.apiUrl('permissions'));
    const payload = permissions.map((p) => this.jsonSerializer.serializeObject(p));
    const options = {
      headers: this.authService.headers(token),
      body: payload,
    };

    return new Promise((resolve, reject) => {
      this.http
        .delete(url.toString(), options)
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetUserIdForEmailAddress(
    token: string,
    emailAdress: string
  ): Promise<string> {
    const params = new URLSearchParams({
      email: emailAdress,
    });
    const url = new URL(
      this.globals.apiUrl(`permissions/assignees?` + params.toString())
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const value = response as string;
          resolve(value);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetPermissionsRelatedToUserRequest(
    token: string
  ): Promise<Permission[]> {
    const params = new URLSearchParams({
      userid: this.authService.userId,
    });
    const url = new URL(
      this.globals.apiUrl('permissions/related?' + params.toString())
    );

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          let values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<Permission>(response[item], Permission)
          );

          // Filter out permissions that are related to enabling user features
          values = values.filter(
            (p) => this.featureService.isPermissionFeatureRelated(p) === false
          );

          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
